import { alpha, Box, Button, Card, CardContent, Stack, Tab, Theme, Typography, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { Cell, Label, Pie, PieChart, PieLabelRenderProps } from 'recharts';

import { CustomTabs, EmptyContent, fNumber, fShortenNumber, useTabs, uuidv4 } from '@zeta-core';
import { ReplyBoldIcon } from '@zeta-core/assets';
import { InvestorService, QueryAPIs } from '@zeta-services';
import {
  IClientCardItem,
  IFactFindEntity,
  IViewFactFindPersonalDetailsRequest,
  IViewFactFindPersonalDetailsResponse,
  IViewFinancialSnapshotRequest,
  IViewFinancialSnapshotResponse,
} from '@zeta-types';
import { getLabel } from '../common/commons';
import { useSourceControlContext } from '../fact-find-layout/sourcecontrol-context-component';

interface ITabView {
  [key: string]: { net?: number | string; data?: { name?: string; value?: number }[] };
}

export const FinancialSnapshot = ({ clientID }: { clientID: string }) => {
  const location = useLocation();
  const state = (location.state || {}) as { entityData: IFactFindEntity; clientData: IClientCardItem };
  const { clientPartnerDetails } = useSourceControlContext() || {};

  const [viewAssetDetails, setViewAssetDetails] = useState<boolean>(false);

  const [tabData, setTabData] = useState<ITabView>({
    ALL: { net: 0, data: [] },
    CLIENT: { net: 0, data: [] },
    PARTNER: { net: 0, data: [] },
    JOINT: { net: 0, data: [] },
  });

  const [detailsData, setDetailsData] = useState<ITabView>({ CLIENT: { net: 0, data: [] }, PARTNER: { net: 0, data: [] }, JOINT: { net: 0, data: [] } });

  const customTabs = useTabs('ALL');

  const [financialSnapshotTabs, setFinancialSnapshotTabs] = useState([{ value: 'ALL', label: 'All' }]);

  const viewFinancialSnapshot = useMutation({
    mutationFn: (payload: IViewFinancialSnapshotRequest) => QueryAPIs().viewFinancialSnapshot(payload),

    onSuccess: (response: AxiosResponse<IViewFinancialSnapshotResponse>) => {
      const { data: responseData } = response;

      const tabs = [...financialSnapshotTabs];
      if (responseData.partner?.name) {
        tabs.push({ value: 'JOINT', label: 'Joint' });
        tabs.push({ value: 'CLIENT', label: responseData.client.name });
        tabs.push({ value: 'PARTNER', label: responseData.partner.name });
      } else {
        tabs.push({ value: 'CLIENT', label: responseData.client.name });
      }

      setFinancialSnapshotTabs(tabs);

      const alltab = [{ name: responseData?.client?.name, value: Number(responseData?.client?.netAmount) }];
      if (responseData?.partner?.name) {
        alltab.push({ name: responseData?.partner?.name, value: Number(responseData?.partner?.netAmount) });
        alltab.push({ name: 'Joint', value: Number(responseData?.joint?.netAmount) });
      }

      const tabd: ITabView = {
        ALL: { net: responseData?.all?.netAmount, data: alltab },

        CLIENT: {
          net: responseData?.client?.netAmount,
          data: [
            { name: 'Assets', value: Number(responseData?.client?.assetAmount) },
            { name: 'Liabilities', value: Number(responseData?.client?.liabilityAmount) },
          ],
        },
      };
      if (responseData?.partner?.name) {
        tabd.PARTNER = {
          net: responseData?.partner?.netAmount,
          data: [
            { name: 'Assets', value: Number(responseData?.partner?.assetAmount) },
            { name: 'Liabilities', value: Number(responseData?.partner?.liabilityAmount) },
          ],
        };
        tabd.JOINT = {
          net: responseData?.joint?.netAmount,
          data: [
            { name: 'Assets', value: Number(responseData?.joint?.assetAmount) },
            { name: 'Liabilities', value: Number(responseData?.joint?.liabilityAmount) },
          ],
        };
      }

      setTabData(tabd);

      const details: ITabView = {
        CLIENT: {
          net: Number(responseData?.client?.assetAmount),
          data: responseData?.client?.assetDetails?.map((e) => {
            return { name: e.assetType, value: Number(e.assetAmount) };
          }),
        },
      };

      if (responseData?.partner?.name) {
        details.PARTNER = {
          net: Number(responseData?.partner?.assetAmount),
          data: responseData?.partner?.assetDetails?.map((e) => {
            return { name: e.assetType, value: Number(e.assetAmount) };
          }),
        };
        details.JOINT = {
          net: Number(responseData?.joint?.assetAmount),
          data: responseData?.joint?.assetDetails?.map((e) => {
            return { name: e.assetType, value: Number(e.assetAmount) };
          }),
        };
      }

      setDetailsData(details);
    },
  });

  const { mutateAsync: viewPersonalDetailsmutateAsync } = useMutation({
    mutationFn: (payload: IViewFactFindPersonalDetailsRequest) => InvestorService().viewPersonalDetails(payload),

    onSuccess: (response: AxiosResponse<IViewFactFindPersonalDetailsResponse>) => {
      const partner = response?.data.partnerDetails;

      viewFinancialSnapshot.mutateAsync({ clientID: clientID, partnerID: partner?.id ?? '' });
    },
  });

  useEffect(() => {
    viewPersonalDetailsmutateAsync({ clientID: clientID });
  }, [viewPersonalDetailsmutateAsync]);

  const openDetails = (e) => {
    if (e.name === 'Assets') setViewAssetDetails(true);
  };

  return (
    <Card>
      <CardContent>
        <Stack sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          <Typography variant="h6">Your Financial Snapshot</Typography>
          <Stack>
            <CustomTabs value={customTabs.value} onChange={customTabs.onChange} variant="scrollable" sx={{ borderRadius: 1 }}>
              {financialSnapshotTabs.map(
                (tab) =>
                  ((viewAssetDetails && tab.value !== 'ALL') || !viewAssetDetails) && (
                    <Tab key={tab.value} value={tab.value} label={getLabel(tab, clientPartnerDetails)} />
                  )
              )}
            </CustomTabs>
          </Stack>
        </Stack>
        <Stack sx={{ alignItems: 'center' }}>
          {Number(tabData?.ALL?.net) > 0 ? (
            <>
              {viewAssetDetails && (
                <>
                  <DetailedDonutChartWithLabels financialData={detailsData[customTabs.value]} selectedTab={customTabs.value} />
                  <Button variant="contained" color="inherit" size="large" startIcon={<ReplyBoldIcon />} onClick={() => setViewAssetDetails(false)}>
                    Back to Snapshot
                  </Button>
                </>
              )}
              {!viewAssetDetails && <DonutChartWithLabels financialData={tabData[customTabs.value]} selectedTab={customTabs.value} openDetails={openDetails} />}
            </>
          ) : (
            <EmptyContent title="No Data Available" />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const renderLabel = (props: PieLabelRenderProps, theme: Theme, colors: string[], selectedTab: string) => {
  const RADIAN = Math.PI / 180;
  const { cx = 0, cy, midAngle, outerRadius = 0, fill, payload, value, index = 0 } = props;
  const cxx = Number(cx);
  const cyy = Number(cy);
  const outer = Number(outerRadius);
  const darkColors = [theme.palette.warning.dark, theme.palette.secondary.dark, theme.palette.info.dark];

  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = cxx + (outer + 20) * cos;
  const sy = cyy + (outer + 20) * sin;
  const mx = cxx + (outer + 20) * cos;
  const my = cyy + (outer + 20) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 80;

  const calculateIndex = index > 0 ? index : 1;
  const ey = my + (calculateIndex - 1) * 45 * -1;
  const textAnchor = cos >= 0 ? 'start' : 'end';
  const isSelectedTabALL = selectedTab === 'ALL';
  return (
    <g>
      <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" strokeWidth={2} />
      <g transform={`translate(${ex - 16}, ${ey - 16})`}>
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="20" cy="20" r="14" fill={colors[index % darkColors.length]} />
          <circle cx="20" cy="20" r="17" stroke={fill} strokeOpacity="0.22" strokeWidth="6" />
          <rect width="16" height="16" transform="translate(12 12)" fill={colors[index % darkColors.length]} />
          <path
            d="M18.4999 19.9998C18.4999 19.602 18.658 19.2205 18.9393 18.9392C19.2206 18.6579 19.6021 18.4998 19.9999 18.4998C20.3977 18.4998 20.7793 18.6579 21.0606 18.9392C21.3419 19.2205 21.4999 19.602 21.4999 19.9998C21.4999 20.3977 21.3419 20.7792 21.0606 21.0605C20.7793 21.3418 20.3977 21.4998 19.9999 21.4998C19.6021 21.4998 19.2206 21.3418 18.9393 21.0605C18.658 20.7792 18.4999 20.3977 18.4999 19.9998Z"
            fill="white"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M13.3333 19.9998C13.3333 21.0932 13.6166 21.4605 14.1833 22.1972C15.3146 23.6665 17.2119 25.3332 19.9999 25.3332C22.7879 25.3332 24.6853 23.6665 25.8166 22.1972C26.3833 21.4612 26.6666 21.0925 26.6666 19.9998C26.6666 18.9065 26.3833 18.5392 25.8166 17.8025C24.6853 16.3332 22.7879 14.6665 19.9999 14.6665C17.2119 14.6665 15.3146 16.3332 14.1833 17.8025C13.6166 18.5398 13.3333 18.9072 13.3333 19.9998ZM19.9999 17.4998C19.3369 17.4998 18.701 17.7632 18.2322 18.2321C17.7633 18.7009 17.4999 19.3368 17.4999 19.9998C17.4999 20.6629 17.7633 21.2988 18.2322 21.7676C18.701 22.2364 19.3369 22.4998 19.9999 22.4998C20.663 22.4998 21.2988 22.2364 21.7677 21.7676C22.2365 21.2988 22.4999 20.6629 22.4999 19.9998C22.4999 19.3368 22.2365 18.7009 21.7677 18.2321C21.2988 17.7632 20.663 17.4998 19.9999 17.4998Z"
            fill="white"
          />
        </svg>
      </g>
      <g transform={`translate(${ex + (cos >= 0 ? 4 : -8) * 12}, ${ey})`} textAnchor={textAnchor}>
        {isSelectedTabALL && (
          <text y={0} fill={darkColors[index % darkColors.length]} style={{ fontSize: '12px', lineHeight: '18px', fontWeight: 700 }}>
            ASSETS & LIABILITIES
          </text>
        )}
        <text y={0} dy={isSelectedTabALL ? 25 : 0} fill={theme.palette.text.primary} style={{ fontSize: '20px', fontWeight: 700, lineHeight: '30px' }}>
          {payload.name}
        </text>
        <text
          y={0}
          dy={isSelectedTabALL ? 50 : 25}
          fill={fill}
          style={{ fontSize: '18px', lineHeight: '28px', fontWeight: 700 }}
        >{`£${fNumber(value, 0)}`}</text>
      </g>
    </g>
  );
};

const DonutChartWithLabels = ({
  financialData,
  selectedTab,
  openDetails,
}: {
  financialData: { net?: number | string; data?: { name?: string; value?: number }[] };
  selectedTab: string;
  openDetails?: (e) => void;
}) => {
  const theme = useTheme();
  const colors = [theme.palette.warning.main, theme.palette.secondary.main, theme.palette.info.main];
  const lighterColors = colors.map((color) => alpha(color, 0.5)); // Lighter colors for the outer ring
  const lightColors = colors.map((color) => alpha(color, 0.2)); // Lighter colors for the outer ring

  return (
    <Box
      sx={{
        '.recharts-pie-label-line': { strokeWidth: 2 },

        '& path:focus, & g:focus': { outline: 'none !important' },
        '.recharts-pie-sector': { strokeWidth: 0 },
      }}
    >
      <PieChart width={1100} height={500}>
        <Pie
          data={financialData?.data}
          cx="50%"
          cy="50%"
          innerRadius={108}
          outerRadius={137}
          dataKey="value"
          label={(props) => renderLabel(props, theme, colors, selectedTab)}
          style={{ cursor: 'pointer' }}
        >
          {financialData?.data?.map((_entry: unknown, index: number) => (
            <Cell
              key={`cell-${uuidv4()}`}
              fill={colors[index % colors.length]}
              onClick={() => {
                if (openDetails) openDetails(_entry);
              }}
            />
          ))}
        </Pie>
        <Pie data={financialData?.data} cx="50%" cy="50%" innerRadius={136} outerRadius={166} dataKey="value" style={{ cursor: 'pointer' }}>
          {financialData?.data?.map((_entry: unknown, index: number) => (
            <Cell
              key={`cell-${uuidv4()}`}
              fill={lighterColors[index % lighterColors.length]}
              onClick={() => {
                if (openDetails) openDetails(_entry);
              }}
            />
          ))}
          <Label content={() => renderCenterLabel(theme, financialData, 'Net Worth')} position="center" />
        </Pie>

        <Pie data={financialData?.data} cx="50%" cy="50%" innerRadius={165} outerRadius={178} dataKey="value" style={{ cursor: 'pointer' }}>
          {financialData?.data?.map((_entry: unknown, index: number) => (
            <Cell
              key={`cell-${uuidv4()}`}
              fill={lightColors[index % colors.length]}
              onClick={() => {
                if (openDetails) openDetails(_entry);
              }}
            />
          ))}
        </Pie>
      </PieChart>
    </Box>
  );
};

const renderDetailsLabel = (props: PieLabelRenderProps, theme: Theme, colors: string[], selectedTab: string, data: any[]) => {
  const RADIAN = Math.PI / 180;
  const { cx = 0, cy, midAngle, outerRadius = 0, fill, payload, value, index = 0 } = props;
  const cxx = Number(cx);
  const cyy = Number(cy);
  const outer = Number(outerRadius);
  const darkColors = [
    theme.palette.success.dark,
    theme.palette.info.dark,
    theme.palette.warning.dark,
    theme.palette.error.dark,
    theme.palette.grey[700],
    theme.palette.secondary.dark,
  ];

  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);

  const SMALL_SLICE_THRESHOLD = 20;
  const BOTTOM_THRESHOLD_ANGEL = 90;

  const isSmall = value < SMALL_SLICE_THRESHOLD;
  const isBottomHalf = midAngle > BOTTOM_THRESHOLD_ANGEL && midAngle < 270;

  const prevSmall = index > 0 && data[index - 1]?.value < SMALL_SLICE_THRESHOLD;
  const nextSmall = index < data?.length - 1 && data[index + 1]?.value < SMALL_SLICE_THRESHOLD;

  const extraOffsetfactor = isBottomHalf ? index * 12 : prevSmall || nextSmall ? index * 8 : 0;
  const lineLength = isSmall ? 40 + extraOffsetfactor : 35;

  const sx = cxx + (outer + 8) * cos;
  const sy = cyy + (outer + 8) * sin;
  const mx = cxx + (outer + lineLength) * cos;
  const my = cyy + (outer + lineLength) * sin;

  const ex = mx + (cos >= 0 ? 1 : -1) * 40;
  let ey = my + (isSmall ? 18 + extraOffsetfactor : 22);

  if (isBottomHalf) ey += 18 + extraOffsetfactor;
  else if (prevSmall) ey += 12;
  else if (nextSmall) ey -= 12;

  const textAnchor = cos >= 0 ? 'start' : 'end';
  return (
    <g>
      <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" strokeWidth={2} />
      <g transform={`translate(${ex - 20}, ${ey - 20})`}>
        <circle cx="20" cy="20" r="5" fill={colors[index % darkColors.length]} />
      </g>
      <g transform={`translate(${ex + (cos >= 0 ? 4 : -8) * 12}, ${ey})`} textAnchor={textAnchor}>
        <text y={0} dy={0} fill={theme.palette.text.primary} style={{ fontSize: '14px', fontWeight: 700, lineHeight: '20px' }}>
          {payload.name}
        </text>
        <text y={0} dy={20} fill={fill} style={{ fontSize: '16px', lineHeight: '28px', fontWeight: 700 }}>{`£${fNumber(value, 0)}`}</text>
      </g>
    </g>
  );
};

const DetailedDonutChartWithLabels = ({
  financialData,
  selectedTab,
  openDetails,
}: {
  financialData: { net?: number | string; data?: { name?: string; value?: number }[] };
  selectedTab: string;
  openDetails?: (e) => void;
}) => {
  const theme = useTheme();
  const colors = [
    theme.palette.success.main,
    theme.palette.info.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.grey[400],
    theme.palette.secondary.main,
  ];
  const lighterColors = colors.map((color) => alpha(color, 0.5)); // Lighter colors for the outer ring
  const lightColors = colors.map((color) => alpha(color, 0.2)); // Lighter colors for the outer ring

  return (
    <Box
      sx={{
        '.recharts-pie-label-line': { strokeWidth: 2 },

        '& path:focus, & g:focus': { outline: 'none !important' },
        '.recharts-pie-sector': { strokeWidth: 0 },
      }}
    >
      <PieChart width={1100} height={500}>
        <Pie
          data={financialData?.data}
          cx="50%"
          cy="50%"
          innerRadius={108}
          outerRadius={137}
          dataKey="value"
          label={(props) => renderDetailsLabel(props, theme, colors, selectedTab, financialData?.data)}
        >
          {financialData?.data?.map((_entry: unknown, index: number) => (
            <Cell
              key={`cell-${uuidv4()}`}
              fill={colors[index % colors.length]}
              onClick={() => {
                if (openDetails) openDetails(_entry);
              }}
            />
          ))}
        </Pie>
        <Pie data={financialData?.data} cx="50%" cy="50%" innerRadius={136} outerRadius={166} dataKey="value">
          {financialData?.data?.map((_entry: unknown, index: number) => (
            <Cell
              key={`cell-${uuidv4()}`}
              fill={lighterColors[index % lighterColors.length]}
              onClick={() => {
                if (openDetails) openDetails(_entry);
              }}
            />
          ))}
          <Label content={() => renderCenterLabel(theme, financialData, 'Assets')} position="center" />
        </Pie>

        <Pie data={financialData?.data} cx="50%" cy="50%" innerRadius={165} outerRadius={178} dataKey="value">
          {financialData?.data?.map((_entry: unknown, index: number) => (
            <Cell
              key={`cell-${uuidv4()}`}
              fill={lightColors[index % colors.length]}
              onClick={() => {
                if (openDetails) openDetails(_entry);
              }}
            />
          ))}
        </Pie>
      </PieChart>
    </Box>
  );
};

const renderCenterLabel = (theme: Theme, financialData: { net?: number | string; data?: { name?: string; value?: number }[] }, label: string) => {
  const { fontWeight, lineHeight } = theme.typography.h6;
  const { fontWeight: fontWeighth2, lineHeight: lineHeighth2 } = theme.typography.h2;

  return (
    <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle">
      <tspan x="50%" dy="-2.0em" style={{ fontSize: '20px', fontWeight, lineHeight, fill: theme.palette.text.disabled }}>
        {label}
      </tspan>
      <tspan x="50%" dy="1.0em" style={{ fontSize: '48px', fontWeight: fontWeighth2, lineHeight: lineHeighth2, fill: theme.palette.text.primary }}>
        £{financialData?.net ? fShortenNumber(financialData?.net) : '0'}
      </tspan>
    </text>
  );
};
